/**
 * FastMatchAudit — /fast-match/audit
 *
 * Student-facing "who saw my shared Fast Match result" page.
 * Cookie-scoped: reads the student's current invite cookie on
 * the server side and returns every share they have ever made
 * plus every view logged against those shares.
 *
 * If the student has no shares (or no invite cookie at all) we
 * render an explanatory empty state — the page never redirects
 * or 401s.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Eye,
  EyeOff,
  Loader2,
  ShieldCheck,
  History,
  Users,
  Handshake,
} from "lucide-react";


function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}


export default function FastMatchAudit() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: d } = await api.get(
          "/course-fit/invite/share/history",
          { withCredentials: true },
        );
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Could not load audit");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (err) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        <div className="font-editorial text-xl">Audit unavailable</div>
        <p className="mt-2 text-sm text-stone-600">{err}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        <Loader2 className="h-5 w-5 animate-spin inline mr-2 text-stone-500" />
        <span className="label-uppercase inline">Loading audit…</span>
      </div>
    );
  }

  const items = data.items || [];

  return (
    <div
      className="max-w-4xl mx-auto px-6 py-10"
      data-testid="fast-match-audit-page"
    >
      <Link
        to="/beta/course-fit"
        className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-900"
        data-testid="fma-back"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Fast Match
      </Link>

      <header className="mt-3">
        <div className="flex items-center gap-2 text-emerald-800">
          <ShieldCheck className="h-4 w-4" />
          <span className="text-[10px] font-underground-sc tracking-widest">
            Your share audit
          </span>
        </div>
        <h1 className="mt-1 font-editorial text-3xl sm:text-4xl">
          Who saw your Fast Match result
        </h1>
        <p className="mt-2 text-sm text-stone-600 max-w-2xl">
          Every counsellor you've shared your result with, and every
          time they've opened it. You can withdraw a share at any
          time from your Fast Match result page — withdrawn shares
          stay listed here for your records.
        </p>
      </header>

      {items.length === 0 ? (
        <div
          className="mt-10 p-8 border border-dashed border-stone-300 rounded-xl text-center text-stone-500"
          data-testid="fma-empty"
        >
          <History className="h-8 w-8 mx-auto text-stone-400" />
          <div className="mt-3 font-editorial text-lg text-stone-800">
            No shares yet
          </div>
          <p className="mt-1 text-sm">
            When you consent to share a Fast Match result with a
            counsellor, it will appear here. Complete Fast Match to
            get started.
          </p>
          <Link to="/beta/course-fit">
            <Button className="mt-4" data-testid="fma-empty-cta">
              Start Fast Match
            </Button>
          </Link>
        </div>
      ) : (
        <ul
          className="mt-8 space-y-4"
          data-testid="fma-list"
        >
          {items.map((it, i) => (
            <li
              key={it.invite_id || i}
              className="p-5 border border-stone-200 rounded-xl bg-white"
              data-testid={`fma-row-${i}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-stone-500 shrink-0" />
                    <div className="font-editorial text-lg text-stone-900 truncate">
                      {it.counsellor_school}
                    </div>
                  </div>
                  {it.counsellor_name && (
                    <div className="mt-0.5 text-[12px] text-stone-500">
                      via {it.counsellor_name}
                    </div>
                  )}
                  <div className="mt-2 text-[11px] font-mono text-stone-500">
                    Shared {fmt(it.consented_at)}
                    {it.withdrawn_at && (
                      <>
                        {" · "}
                        <span className="text-amber-700">
                          Withdrawn {fmt(it.withdrawn_at)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  {it.share_status === "shared" ? (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-1 text-[11px] font-underground-sc tracking-wider text-emerald-800"
                      data-testid={`fma-status-${i}`}
                    >
                      <Eye className="h-3 w-3" /> Active
                    </span>
                  ) : (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-stone-50 border border-stone-200 px-2.5 py-1 text-[11px] font-underground-sc tracking-wider text-stone-600"
                      data-testid={`fma-status-${i}`}
                    >
                      <EyeOff className="h-3 w-3" /> Withdrawn
                    </span>
                  )}
                </div>
              </div>

              <div className="mt-4 border-t border-stone-100 pt-3">
                <div className="flex items-center justify-between">
                  <div className="text-[11px] font-underground-sc tracking-widest text-stone-500">
                    Counsellor opens
                  </div>
                  <div
                    className="text-[12px] font-mono text-stone-700"
                    data-testid={`fma-view-count-${i}`}
                  >
                    {it.view_count} view{it.view_count === 1 ? "" : "s"}
                    {it.last_viewed_at && (
                      <> · last {fmt(it.last_viewed_at)}</>
                    )}
                  </div>
                </div>
                {it.views && it.views.length > 0 && (
                  <ul
                    className="mt-2 space-y-1 max-h-40 overflow-y-auto pr-1"
                    data-testid={`fma-views-${i}`}
                  >
                    {it.views.map((v, j) => (
                      <li
                        key={j}
                        className="flex items-center justify-between text-[11px] font-mono text-stone-500"
                      >
                        <span className="truncate">
                          {v.actor_email || "counsellor"}
                        </span>
                        <span className="tabular-nums">{fmt(v.viewed_at)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-6 text-[11px] font-underground-sc tracking-wider text-stone-400">
        This page is scoped to the browser holding your Fast Match
        invite cookie. Clearing cookies will hide the list until you
        redeem an invite again.
      </p>

      {/* Cross-sell to the Reverse Job Board — this student
          audience is warm (they've already opted into sharing with
          a counsellor). */}
      <Link
        to="/me/talent-profile"
        data-testid="fma-talent-cta"
        className="mt-6 flex items-start gap-3 p-4 rounded-xl border border-[#4C1D95]/25 bg-[#F5F0FF]/60 hover:bg-[#F5F0FF] transition"
      >
        <Handshake className="h-5 w-5 text-[#4C1D95] shrink-0 mt-0.5" />
        <div className="min-w-0">
          <div className="font-editorial text-base text-stone-900">
            Want firms to find you too?
          </div>
          <div className="mt-0.5 text-[12px] text-stone-600 leading-snug">
            Turn on your Talent Profile and Verified Partner firms
            (Goldman, Freshfields, Deloitte…) can request an intro.
            Names & email stay hidden until <span className="font-medium">you both</span> say yes.
          </div>
        </div>
      </Link>
    </div>
  );
}
